import { createBrowserRouter, Navigate, replace } from 'react-router';
import { AppLayout, ErrorFallback, WizardLayout } from '@/components/shared';
import { ROUTES } from '@/lib/constants/routes';
import { DesignSystemGuard } from './app/design-system/route-guard';
import { IntelligenceProfileGuard } from './app/intelligence-audience/route-guard';
import { HomeGuard } from './app/home-page/route-guard';

/** The old Explore page's paths (NPJB-790): it is gone, so they open the audience page. */
const toAudiences = <Navigate to={ROUTES.intelligenceAudiences} replace />;

export const router = createBrowserRouter([
  {
    path: '/',
    element: <AppLayout />,
    errorElement: <ErrorFallback />,
    children: [
      {
        element: <HomeGuard />,
        children: [
          {
            index: true,
            lazy: () => import('./app/home-page/page'),
          },
        ],
      },
      {
        path: 'campaigns',
        lazy: () => import('./app/campaigns-home/page'),
      },
      {
        path: 'campaigns/new',
        lazy: () => import('./app/campaign-create/page'),
      },
      {
        path: 'campaigns/:campaignId',
        lazy: () => import('./app/campaign-detail/page'),
      },
      {
        path: 'intelligence/campaign',
        lazy: () => import('./app/intelligence-campaign/page'),
      },
      {
        element: <WizardLayout />,
        children: [
          {
            path: 'audience-builder/:id?',
            lazy: () => import('./app/audience-builder/page'),
          },
          {
            path: 'audience-products/:id?',
            lazy: () => import('./app/audience-products/page'),
          },
          {
            path: 'audience-content/:id?',
            lazy: () => import('./app/audience-content/page'),
          },
          {
            path: 'audience-review/:id?',
            lazy: () => import('./app/audience-review/page'),
          },
        ],
      },
      {
        path: 'audience-request-confirmation/:id',
        lazy: () => import('./app/audience-request-confirmation/page'),
      },
      {
        path: 'audiences/:id',
        lazy: () => import('./app/audience-overview/page'),
      },
      {
        path: 'audiences/:id/edit',
        lazy: () => import('./app/audience-request-edit/page'),
      },
      {
        path: 'intelligence',
        children: [
          {
            // The audience page and its profile pages are one feature, gated by
            // `intelligence-audience-switcher`.
            element: <IntelligenceProfileGuard />,
            children: [
              // The bare path was the old Explore page (NPJB-790).
              { index: true, element: toAudiences },
              {
                // NPJB-586. The bare path lands on the default audience.
                path: 'audiences',
                lazy: () => import('./app/intelligence-audience/page'),
              },
              {
                path: 'audiences/:audienceId',
                lazy: () => import('./app/intelligence-audience/page'),
              },
              {
                // NPJB-676. The audience travels with the account (AC-2).
                path: 'audiences/:audienceId/accounts/:domHash',
                lazy: () => import('./app/account-profile/page'),
              },
              {
                // Identity is in the path, matching the API exactly: an edited URL must
                // not be able to produce a play computed against filters the page does
                // not claim.
                path: 'audiences/:audienceId/accounts/:domHash/contacts/:personKey',
                lazy: () => import('./app/contact-profile/page'),
              },
            ],
          },
        ],
      },
      { path: 'explore/*', element: toAudiences },
      { path: 'explore', element: toAudiences },
      {
        element: <DesignSystemGuard />,
        children: [
          {
            path: 'design-system',
            lazy: () => import('./app/design-system/page'),
          },
        ],
      },
    ],
  },
  {
    errorElement: <ErrorFallback />,
    lazy: async () => {
      const { AuthLayout } = await import('@/components/shared/auth-layout');
      return { element: <AuthLayout /> };
    },
    children: [
      { path: 'login', lazy: () => import('./app/auth/login/page') },
      { path: 'signup', lazy: () => import('./app/auth/signup/page') },
      { path: 'forgot-password', lazy: () => import('./app/auth/forgot-password/page') },
      { path: 'set-password', lazy: () => import('./app/auth/set-password/page') },
      { path: 'verify-email', lazy: () => import('./app/auth/verify-email/page') },
    ],
  },
  {
    // NPJB-623 — the onboarding flow. Its own layout rather than AuthLayout:
    // these screens bring their own card (the prototype's white card on the
    // pale-blue field), and nesting that inside the app's frosted auth card
    // would be one card too many. The signed-in redirect is kept.
    //
    // `/signup` forwards here when the `onboarding-flow` flag is on — a
    // separate route rather than a branch inside the old page, so flag-off
    // leaves that page byte-for-byte as it is today.
    errorElement: <ErrorFallback />,
    lazy: async () => {
      const { OnboardingAuthLayout } = await import('@/app/onboarding/components/shared');
      return { element: <OnboardingAuthLayout /> };
    },
    children: [
      { path: 'onboarding/signup', lazy: () => import('./app/onboarding/signup/page') },
      { path: 'onboarding/verify', lazy: () => import('./app/onboarding/verify/page') },
    ],
  },
  {
    // NPJB-623 — the flow itself, which needs a session but not the app shell:
    // these screens are full-bleed with their own chrome and footer bar, so
    // AppLayout's header would sit above a set of questions whose whole point
    // is that there is nothing else to do yet.
    //
    // `/onboarding` is a resolver, not a screen — it asks the server which step
    // this account is on. The static `welcome` path outranks `:stepKey`, and so
    // do `signup`/`verify` in the block above, so none of them collide.
    errorElement: <ErrorFallback />,
    lazy: async () => {
      const { OnboardingSessionLayout } = await import('@/app/onboarding/components/shared');
      return { element: <OnboardingSessionLayout /> };
    },
    children: [
      { path: 'onboarding', lazy: () => import('./app/onboarding/page') },
      { path: 'onboarding/welcome', lazy: () => import('./app/onboarding/welcome/page') },
      // The same pages pinned to one ICP (NPJB-726): how Intelligence edits it.
      // Two segments, so neither collides with the one-segment routes above.
      { path: 'onboarding/:icpId/welcome', lazy: () => import('./app/onboarding/welcome/page') },
      // Step 6 (NPJB-757): a preview after completion, outside the question layout.
      // Static `preview` outranks `:stepKey`, so neither collides.
      { path: 'onboarding/preview', lazy: () => import('./app/onboarding/preview/page') },
      { path: 'onboarding/:icpId/preview', lazy: () => import('./app/onboarding/preview/page') },
      {
        lazy: async () => {
          const { OnboardingFlowLayout } = await import('@/app/onboarding/components/shared');
          return { element: <OnboardingFlowLayout /> };
        },
        children: [
          { path: 'onboarding/:stepKey', lazy: () => import('./app/onboarding/steps/page') },
          { path: 'onboarding/:icpId/:stepKey', lazy: () => import('./app/onboarding/steps/page') },
        ],
      },
    ],
  },
  {
    // Follow-confirm is token-gated, NOT session-gated: an invitee may be
    // logged in (existing user) or logged out (brand-new). It uses the same card
    // shell as AuthLayout but WITHOUT the authed→home redirect, so a logged-in
    // invitee can still confirm (NPJB-439).
    errorElement: <ErrorFallback />,
    lazy: async () => {
      const { ConfirmLayout } = await import('@/components/shared/auth-layout');
      return { element: <ConfirmLayout /> };
    },
    children: [{ path: 'confirm-follow', lazy: () => import('./app/auth/confirm-follow/page') }],
  },
  {
    // Standalone — no layout wrapper. User is not yet authenticated when they
    // arrive here; we consume the HMAC token, set cookies, and push into the app.
    path: 'impersonate',
    errorElement: <ErrorFallback />,
    lazy: () => import('./app/auth/impersonate/page'),
  },
  {
    // NPJB-817 — `/reset-password` is the name people (and older emails) use; the page lives
    // at `/set-password`. A loader, not `<Navigate>`, so the `?token=` query comes along.
    // `replace`, not `redirect`: the old URL leaves history, so Back doesn't bounce here.
    path: 'reset-password',
    loader: ({ request }) => replace(`/set-password${new URL(request.url).search}`),
  },
]);
